import { useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "./auth";
import { extractTenantFromUrl } from "../utils/themeUtils";
import { saveToStorage, getFromStorage } from "../utils/storageUtils";
import { SESSION_STORAGE_KEYS } from "../constants/themeConstants";

/**
 * Hook customizado para gerenciar o carregamento de temas
 * Separa a lógica de carregamento de tema do componente App
 */
export const useThemeLoader = () => {
  const {
    loadTenantTheme,
    setWhitelabelEnabled,
    tenantTheme,
    themeInitialized,
  } = useTheme();
  const { user, getTenantInfo } = useAuth();

  /**
   * Carrega tema baseado na URL (executa na inicialização)
   */
  useEffect(() => {
    const loadThemeFromUrl = async () => {
      try {
        // Para teste, usar host estático:
        // let host = "lbconsultoria.data4company.com.br";
        const host = window.location.host;
        const tenantIdentifier = extractTenantFromUrl(host);

        // Só carrega se tiver tenant na URL, usuário não estiver logado 
        // E o tema atual for diferente do tenant da URL
        if (tenantIdentifier && !user?.id) {
          if (tenantTheme?.id !== tenantIdentifier) {
            await loadTenantTheme(tenantIdentifier, false);
            setWhitelabelEnabled(true);

            // Salvar informações de controle
            saveToStorage(SESSION_STORAGE_KEYS.LAST_HOST, host);
            saveToStorage(SESSION_STORAGE_KEYS.LAST_TENANT, tenantIdentifier);
            saveToStorage(SESSION_STORAGE_KEYS.THEME_LOADED, true);
          }
        }
      } catch (error) {
        console.warn("⚠️ Erro ao carregar tema da URL:", error);
      }
    };

    loadThemeFromUrl();
  }, [loadTenantTheme, setWhitelabelEnabled, user?.id, tenantTheme?.id]);

  /**
   * Carrega tema quando usuário faz login ou muda de tenant
   */
  useEffect(() => {
    const loadThemeOnLogin = async () => {
      const tenantInfo = getTenantInfo();

      if (!tenantInfo?.identificador || !user?.id) return;

      // Verificar se o tema atual já corresponde ao tenant do usuário
      if (tenantTheme?.id === tenantInfo.identificador) {
        // Se já está carregado e correto, apenas garante que os dados de sessão estão atualizados
        const lastTenant = getFromStorage(SESSION_STORAGE_KEYS.LAST_TENANT);
        if (lastTenant !== tenantInfo.identificador) {
             saveToStorage(SESSION_STORAGE_KEYS.LAST_TENANT, tenantInfo.identificador);
             saveToStorage(SESSION_STORAGE_KEYS.LAST_USER_ID, user.id.toString());
             saveToStorage(SESSION_STORAGE_KEYS.THEME_LOADED, true);
        }
        return;
      }

      try {
        console.log("🔄 Carregando tema do usuário/tenant:", tenantInfo.identificador);
        await loadTenantTheme(tenantInfo.identificador, false);
        setWhitelabelEnabled(true);

        // Atualizar informações de controle
        saveToStorage(SESSION_STORAGE_KEYS.LAST_TENANT, tenantInfo.identificador);
        saveToStorage(SESSION_STORAGE_KEYS.LAST_USER_ID, user.id.toString());
        saveToStorage(SESSION_STORAGE_KEYS.THEME_LOADED, true);
      } catch (error) {
        console.warn("❌ Erro ao carregar tema no login:", error);
      }
    };

    loadThemeOnLogin();
  }, [
    user?.id,
    getTenantInfo,
    loadTenantTheme,
    setWhitelabelEnabled,
    tenantTheme?.id, // Depender apenas do ID para evitar loops por mudanças profundas de objeto
  ]);

  /**
   * Detecta mudanças de URL para atualizar tema em tempo real
   */
  useEffect(() => {
    const handleUrlChange = async () => {
      if (!user?.id) return;

      const currentHost = window.location.host;
      const currentTenant = extractTenantFromUrl(currentHost);
      const lastHost = getFromStorage(SESSION_STORAGE_KEYS.LAST_HOST);

      if (currentHost !== lastHost && currentTenant) {
        try {
          await loadTenantTheme(currentTenant, true);
          setWhitelabelEnabled(true);

          saveToStorage(SESSION_STORAGE_KEYS.LAST_HOST, currentHost);
          saveToStorage(SESSION_STORAGE_KEYS.LAST_TENANT, currentTenant);
        } catch (error) {
          console.warn("❌ Erro ao atualizar tema por mudança de URL:", error);
        }
      }
    };

    // Escutar mudanças de URL
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [user?.id, loadTenantTheme, setWhitelabelEnabled]);
};
